import { getUserIdFromToken } from "./Auth";

/*
 * A small stale-while-revalidate cache for read-only GET data.
 *
 * Entries live in memory and in sessionStorage, so they survive navigating away and reloading the
 * tab but disappear when the tab is closed. Keys are scoped to the logged-in user, and logging out
 * clears everything. Concurrent requests for the same key share one HTTP call, which matters for
 * aggregate queries that take tens of seconds on the database.
 */

export interface CacheEntry<T> {
	data: T;
	storedAt: number;
}

const STORAGE_PREFIX = "pm-cache:";

const memory = new Map<string, CacheEntry<unknown>>();
const inFlight = new Map<string, Promise<unknown>>();
// Bumped on clear, so a request that started before logout cannot write its result back.
let generation = 0;

const scopedKey = (key: string) =>
	`${STORAGE_PREFIX}${getUserIdFromToken() || "anonymous"}:${key}`;

export const readCache = <T>(key: string): CacheEntry<T> | undefined => {
	const storageKey = scopedKey(key);
	const hit = memory.get(storageKey);
	if (hit) return hit as CacheEntry<T>;
	try {
		const raw = sessionStorage.getItem(storageKey);
		if (!raw) return undefined;
		const entry = JSON.parse(raw) as CacheEntry<T>;
		memory.set(storageKey, entry);
		return entry;
	} catch {
		return undefined;
	}
};

export const isFresh = (
	entry: CacheEntry<unknown> | undefined,
	ttlMs: number
): boolean => !!entry && Date.now() - entry.storedAt < ttlMs;

const writeCache = <T>(storageKey: string, data: T): CacheEntry<T> => {
	const entry = { data, storedAt: Date.now() };
	memory.set(storageKey, entry);
	try {
		sessionStorage.setItem(storageKey, JSON.stringify(entry));
	} catch {
		// Storage full or blocked: the in-memory copy still serves this session.
	}
	return entry;
};

/** Runs `fetcher` unless the same key is already in flight, and caches the result. */
export const fetchCached = <T>(
	key: string,
	fetcher: () => Promise<T>
): Promise<CacheEntry<T>> => {
	const storageKey = scopedKey(key);
	const pending = inFlight.get(storageKey);
	if (pending) return pending as Promise<CacheEntry<T>>;

	const startedIn = generation;
	const request = fetcher()
		.then((data) =>
			startedIn === generation
				? writeCache(storageKey, data)
				: { data, storedAt: Date.now() }
		)
		.finally(() => {
			if (inFlight.get(storageKey) === request) {
				inFlight.delete(storageKey);
			}
		});
	inFlight.set(storageKey, request);
	return request;
};

export const clearRequestCache = () => {
	generation += 1;
	memory.clear();
	inFlight.clear();
	try {
		Object.keys(sessionStorage)
			.filter((key) => key.startsWith(STORAGE_PREFIX))
			.forEach((key) => sessionStorage.removeItem(key));
	} catch {
		// Storage blocked: nothing was persisted there either.
	}
};
